import React, { useState, useEffect } from "react";
import {
  Typography,
  Paper,
  Box,
  Button,
  TextField,
  Grid,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Alert,
  CircularProgress,
  Tooltip,
  Badge,
} from "@mui/material";
import {
  Search as SearchIcon,
  Add as AddIcon,
  CloudUpload as UploadIcon,
  Visibility as ViewIcon,
  Refresh as RefreshIcon,
  Edit as EditIcon,
  Lock as LockIcon,
  Warning as WarningIcon,
  AdminPanelSettings as AdminIcon,
  LockOpen as UnlockIcon,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { orderService } from "../../services/orderService";
import { useAuth } from "../../contexts/AuthContext";
import EFaxUploadModal from "../../components/EFaxUploadModal";

const OrdersList = () => {
  const [loading, setLoading] = useState(true);
  const [orders, setOrders] = useState([]);
  const [error, setError] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [editabilityFilter, setEditabilityFilter] = useState("all");
  const [efaxModalOpen, setEfaxModalOpen] = useState(false);
  const [ordersWithEditInfo, setOrdersWithEditInfo] = useState([]);
  const [loadingEditInfo, setLoadingEditInfo] = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  const navigate = useNavigate();
  const { user } = useAuth();

  const fetchOrders = async (showLoading = true) => {
    if (showLoading) setLoading(true);
    setError("");
    try {
      console.log("Fetching orders...");
      const data = await orderService.getAllOrders();
      console.log(`Received ${data.length} orders`);
      setOrders(data || []);

      // ENHANCED: Fetch edit information with better logic
      if (
        user?.role === "super_admin" ||
        user?.role === "admin" ||
        user?.role === "facility_admin"
      ) {
        await fetchEditInformation(data);
      }
    } catch (err) {
      console.error("Error fetching orders:", err);
      setError("Failed to load orders. Please try again later.");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const fetchEditInformation = async (ordersList) => {

    setLoadingEditInfo(true);
    try {
      const ordersWithEdit = [];

      // Process orders in batches to avoid overwhelming the API
      const batchSize = 10;
      for (let i = 0; i < ordersList.length; i += batchSize) {
        const batch = ordersList.slice(i, i + batchSize);

        const batchPromises = batch.map(async (order) => {
          try {
            // ENHANCED: Better edit permission logic based on user role and order status
            const editInfo = determineEditPermissions(order, user);


            return {
              ...order,
              editInfo,
            };
          } catch (error) {
            console.warn(
              `Could not determine edit info for order ${order.id}:`,
              error
            );
            return {
              ...order,
              editInfo: {
                canEdit: false,
                canForceEdit: false,
                requiresForceEdit: true,
                orderStatus: order.status || "unknown",
                editReason: "Error determining permissions",
              },
            };
          }
        });

        const batchResults = await Promise.all(batchPromises);
        ordersWithEdit.push(...batchResults);
      }


      setOrdersWithEditInfo(ordersWithEdit);
    } catch (error) {
      console.error("Error fetching edit information:", error);
      // If we can't get edit info, just use the original orders with default permissions
      setOrdersWithEditInfo(
        ordersList.map((order) => ({
          ...order,
          editInfo: determineEditPermissions(order, user),
        }))
      );
    } finally {
      setLoadingEditInfo(false);
    }
  };

  // FIXED: Comprehensive edit permission determination for facility admins
  const determineEditPermissions = (order, currentUser) => {

    if (!currentUser || !order) {
      return {
        canEdit: false,
        canForceEdit: false,
        requiresForceEdit: false,
        orderStatus: order?.status || "unknown",
        userRole: currentUser?.role || "unknown",
        editReason: "Missing data",
      };
    }

    const orderStatus = order.status;
    const userRole = currentUser.role;

    // FIXED: Enhanced permissions for different user roles
    switch (userRole) {
      case "super_admin":
        return {
          canEdit: true,
          canForceEdit: true,
          requiresForceEdit: false,
          orderStatus: orderStatus,
          userRole: userRole,
          editReason: "Super admin - full access",
        };

      case "facility_admin":

        // CRITICAL FIX: Facility admins should have FULL edit access to ALL orders in their facility
        // Check if user has facility_id
        if (!currentUser.facility_id) {
          return {
            canEdit: false,
            canForceEdit: false,
            requiresForceEdit: true,
            orderStatus: orderStatus,
            userRole: userRole,
            editReason: "No facility assigned to user",
          };
        }

        // Check if order has facility_id
        if (!order.facility_id) {
          // FIXED: Allow facility admins to edit orders without facility_id
          return {
            canEdit: true,
            canForceEdit: true,
            requiresForceEdit: false,
            orderStatus: orderStatus,
            userRole: userRole,
            editReason: "Facility admin - order has no facility restriction",
          };
        }

        // CRITICAL: Compare facility IDs correctly (handle both string and number types)
        const userFacilityId = parseInt(currentUser.facility_id);
        const orderFacilityId = parseInt(order.facility_id);


        if (userFacilityId === orderFacilityId) {
          return {
            canEdit: true, // FIXED: Always true for facility admin in their facility
            canForceEdit: true,
            requiresForceEdit: false,
            orderStatus: orderStatus,
            userRole: userRole,
            editReason: `Facility admin - facility ${userFacilityId} - FULL ACCESS`,
          };
        } else {
          return {
            canEdit: false,
            canForceEdit: false,
            requiresForceEdit: true,
            orderStatus: orderStatus,
            userRole: userRole,
            editReason: `Different facility (user: ${userFacilityId}, order: ${orderFacilityId})`,
          };
        }

      case "admin":
        return {
          canEdit: true,
          canForceEdit: true,
          requiresForceEdit: false,
          orderStatus: orderStatus,
          userRole: userRole,
          editReason: "Admin - full access",
        };

      case "medical_professional":
        // Medical professionals can only edit created and assigned orders assigned to them
        const isMPOrder =
          order.medical_professional_id ===
          currentUser.medical_professional?.id;
        const canMPEdit =
          isMPOrder && ["created", "assigned"].includes(orderStatus);


        return {
          canEdit: canMPEdit,
          canForceEdit: false,
          requiresForceEdit: !canMPEdit,
          orderStatus: orderStatus,
          userRole: userRole,
          editReason: canMPEdit ? "MP - assigned order" : "MP - limited access",
        };

      default:
        return {
          canEdit: false,
          canForceEdit: false,
          requiresForceEdit: true,
          orderStatus: orderStatus,
          userRole: userRole,
          editReason: "No edit permissions",
        };
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchOrders(false);
  };

  useEffect(() => {
    fetchOrders();
  }, [user]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const getStatusChip = (status) => {
    let color = "default";
    switch (status) {
      case "completed":
        color = "success";
        break;
      case "in_progress":
        color = "primary";
        break;
      case "assigned":
        color = "info";
        break;
      case "created":
        color = "warning";
        break;
      case "cancelled":
        color = "error";
        break;
      default:
        color = "default";
    }

    return (
      <Chip
        label={status?.replace("_", " ") || "unknown"}
        color={color}
        size="small"
      />
    );
  };

  // FIXED: Better editability chip with clearer messaging for facility admins
  const getEditabilityChip = (editInfo) => {
    if (!editInfo) {
      return (
        <Chip
          icon={<LockIcon />}
          label="Unknown"
          size="small"
          variant="outlined"
        />
      );
    }

    if (editInfo.canEdit) {
      return (
        <Chip
          icon={<UnlockIcon />}
          label="Editable"
          color="success"
          size="small"
          variant="outlined"
        />
      );
    }

    if (editInfo.canForceEdit) {
      return (
        <Chip
          icon={<AdminIcon />}
          label="Admin Edit"
          color="warning"
          size="small"
          variant="outlined"
        />
      );
    }

    return (
      <Chip
        icon={<LockIcon />}
        label="Read Only"
        color="error"
        size="small"
        variant="outlined"
      />
    );
  };

  // FIXED: Better tooltip messages for facility admins
  const getEditTooltip = (editInfo) => {
    if (!editInfo) return "Edit status unknown";

    if (editInfo.canEdit) {
      return `${editInfo.editReason || "This order can be edited normally"}`;
    }

    if (editInfo.canForceEdit) {
      return `${
        editInfo.editReason || "Administrator override available for this order"
      }`;
    }

    return editInfo.editReason || "This order cannot be edited";
  };

  // Improved getPatientName function with better error handling
  const getPatientName = (order) => {
    if (!order) return "N/A";

    if (order.patient) {
      const firstName = order.patient.first_name || "";
      const lastName = order.patient.last_name || "";

      if (firstName.startsWith("gAAAAA") || lastName.startsWith("gAAAAA")) {
        return `Patient #${order.patient.id || order.patient_id || "Unknown"}`;
      }

      if (
        firstName === "Error" ||
        lastName === "Error" ||
        firstName === "Unknown" ||
        lastName === "Unknown"
      ) {
        return `Patient #${order.patient.id || order.patient_id || "Unknown"}`;
      }

      if (firstName && lastName) {
        return `${firstName} ${lastName}`;
      } else if (firstName) {
        return firstName;
      } else if (lastName) {
        return lastName;
      }
    }

    return `Patient #${order.patient_id || "Unknown"}`;
  };

  // Improved getPatientAddress function with better error handling
  const getPatientAddress = (order) => {
    if (!order) return "N/A";

    if (order.patient) {
      const address = order.patient.address || "";
      const city = order.patient.city || "";
      const state = order.patient.state || "";
      const zipCode = order.patient.zip_code || "";

      if (address.startsWith("gAAAAA")) {
        return "Address available in order details";
      }

      if (
        address === "Error loading address" ||
        address === "Address not provided"
      ) {
        return "Address available in order details";
      }

      let fullAddress = address;

      if (city && city !== "Unknown") {
        fullAddress += `, ${city}`;
      }

      if (state && state !== "CA" && state !== "Unknown") {
        fullAddress += `, ${state}`;
      }

      if (zipCode && zipCode !== "00000") {
        fullAddress += ` ${zipCode}`;
      }

      if (fullAddress && fullAddress !== "") {
        return fullAddress;
      }
    }

    return "Address available in order details";
  };

  // Better order validation
  const isValidOrder = (order) => {
    return (
      order &&
      typeof order === "object" &&
      order.id &&
      order.order_number &&
      order.status
    );
  };

  // Enhanced filtering logic
  const filteredOrders = (
    ordersWithEditInfo.length > 0 ? ordersWithEditInfo : orders
  ).filter((order) => {
    if (!isValidOrder(order)) {
      console.warn("Invalid order found:", order);
      return false;
    }

    const orderNumber = order.order_number || "";
    const patientName = getPatientName(order);
    const patientAddress = getPatientAddress(order);

    const matchesSearch =
      orderNumber.toLowerCase().includes(searchQuery.toLowerCase()) ||
      patientName.toLowerCase().includes(searchQuery.toLowerCase()) ||
      patientAddress.toLowerCase().includes(searchQuery.toLowerCase());

    const matchesStatus =
      statusFilter === "all" || order.status === statusFilter;

    // ENHANCED: Editability filter with better logic
    let matchesEditability = true;
    if (editabilityFilter !== "all" && order.editInfo) {
      switch (editabilityFilter) {
        case "editable":
          matchesEditability = order.editInfo.canEdit;
          break;
        case "force_edit":
          matchesEditability =
            order.editInfo.canForceEdit && !order.editInfo.canEdit;
          break;
        case "locked":
          matchesEditability =
            !order.editInfo.canEdit && !order.editInfo.canForceEdit;
          break;
        default:
          matchesEditability = true;
      }
    }

    return matchesSearch && matchesStatus && matchesEditability;
  });

  if (loading && orders.length === 0) {
    return (
      <Box display="flex" flexDirection="column" alignItems="center" mt={10}>
        <CircularProgress size={60} thickness={5} />
        <Typography variant="h6" mt={3}>
          Loading orders...
        </Typography>
        {loadingEditInfo && (
          <Typography variant="body2" color="text.secondary" mt={1}>
            Loading edit permissions...
          </Typography>
        )}
      </Box>
    );
  }

  return (
    <Box>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={3}
      >
        <Typography variant="h4">Orders</Typography>
        <Box>
          <Button
            variant="outlined"
            startIcon={
              refreshing ? <CircularProgress size={20} /> : <RefreshIcon />
            }
            onClick={handleRefresh}
            sx={{ mr: 2 }}
            disabled={loading || refreshing}
          >
            {refreshing ? "Refreshing..." : "Refresh"}
          </Button>

          {/* FIXED: Only show admin actions for admin roles */}
          {(user?.role === "super_admin" ||
            user?.role === "admin" ||
            user?.role === "facility_admin") && (
            <>
              <Button
                variant="outlined"
                color="primary"
                startIcon={<UploadIcon />}
                onClick={() => setEfaxModalOpen(true)}
                sx={{ mr: 2 }}
              >
                Upload eFax
              </Button>
              <Button
                variant="contained"
                color="primary"
                startIcon={<AddIcon />}
                onClick={() => navigate("/orders/create")}
              >
                Create Order
              </Button>
            </>
          )}
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {loadingEditInfo && (
        <Alert severity="info" sx={{ mb: 3 }}>
          <Box display="flex" alignItems="center" gap={1}>
            <CircularProgress size={16} />
            Loading edit permissions for enhanced filtering...
          </Box>
        </Alert>
      )}

      <Paper elevation={3} sx={{ p: 3, mb: 3 }}>
        <Grid container spacing={2}>
          <Grid  size={{ xs: 12, md: 5 }}>
            <TextField
              fullWidth
              placeholder="Search orders by number, patient, or address"
              variant="outlined"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid  size={{ xs: 12, md: 3 }}>
            <FormControl fullWidth variant="outlined">
              <InputLabel>Status</InputLabel>
              <Select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                label="Status"
              >
                <MenuItem value="all">All Statuses</MenuItem>
                <MenuItem value="created">Created</MenuItem>
                <MenuItem value="assigned">Assigned</MenuItem>
                <MenuItem value="in_progress">In Progress</MenuItem>
                <MenuItem value="completed">Completed</MenuItem>
                <MenuItem value="cancelled">Cancelled</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          {(user?.role === "super_admin" ||
            user?.role === "admin" ||
            user?.role === "facility_admin") && (
            <Grid  size={{ xs: 12, md: 4 }}>
              <FormControl fullWidth variant="outlined">
                <InputLabel>Editability</InputLabel>
                <Select
                  value={editabilityFilter}
                  onChange={(e) => setEditabilityFilter(e.target.value)}
                  label="Editability"
                >
                  <MenuItem value="all">All Orders</MenuItem>
                  <MenuItem value="editable">
                    <Box display="flex" alignItems="center" gap={1}>
                      <UnlockIcon fontSize="small" />
                      Editable
                    </Box>
                  </MenuItem>
                  <MenuItem value="force_edit">
                    <Box display="flex" alignItems="center" gap={1}>
                      <AdminIcon fontSize="small" />
                      Admin Edit Required
                    </Box>
                  </MenuItem>
                  <MenuItem value="locked">
                    <Box display="flex" alignItems="center" gap={1}>
                      <LockIcon fontSize="small" />
                      Read Only
                    </Box>
                  </MenuItem>
                </Select>
              </FormControl>
            </Grid>
          )}
        </Grid>
      </Paper>

      {loading && <LinearProgress sx={{ mb: 2 }} />}

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Order #</TableCell>
              <TableCell>Patient</TableCell>
              <TableCell>Address</TableCell>
              <TableCell>Status</TableCell>
              {(user?.role === "super_admin" ||
                user?.role === "admin" ||
                user?.role === "facility_admin") && (
                <TableCell>Edit Status</TableCell>
              )}
              <TableCell>Date</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredOrders.length > 0 ? (
              filteredOrders
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((order) => (
                  <TableRow key={order.id || Math.random().toString()}>
                    <TableCell component="th" scope="row">
                      {order.order_number || "N/A"}
                    </TableCell>
                    <TableCell>{getPatientName(order)}</TableCell>
                    <TableCell>{getPatientAddress(order)}</TableCell>
                    <TableCell>{getStatusChip(order.status)}</TableCell>
                    {(user?.role === "super_admin" ||
                      user?.role === "admin" ||
                      user?.role === "facility_admin") && (
                      <TableCell>
                        <Tooltip title={getEditTooltip(order.editInfo)}>
                          <span>{getEditabilityChip(order.editInfo)}</span>
                        </Tooltip>
                      </TableCell>
                    )}
                    <TableCell>
                      {order.created_at
                        ? new Date(order.created_at).toLocaleDateString()
                        : "N/A"}
                    </TableCell>
                    <TableCell align="right">
                      <Box display="flex" gap={1} justifyContent="flex-end">
                        <Tooltip title="View order details">
                          <IconButton
                            color="primary"
                            onClick={() => navigate(`/orders/${order.id}`)}
                          >
                            <ViewIcon />
                          </IconButton>
                        </Tooltip>

                        {/* FIXED: Edit button with proper facility admin permissions */}
                        {order.editInfo && (
                          <Tooltip
                            title={
                              order.editInfo.canEdit
                                ? "Edit this order"
                                : order.editInfo.canForceEdit
                                ? "Administrator edit available"
                                : "Cannot edit this order"
                            }
                          >
                            <span>
                              <IconButton
                                color={
                                  order.editInfo.canEdit
                                    ? "success"
                                    : order.editInfo.canForceEdit
                                    ? "warning"
                                    : "default"
                                }
                                onClick={() => navigate(`/orders/${order.id}`)}
                                disabled={
                                  !order.editInfo.canEdit &&
                                  !order.editInfo.canForceEdit
                                }
                              >
                                {order.editInfo.canEdit ? (
                                  <UnlockIcon />
                                ) : order.editInfo.canForceEdit ? (
                                  <Badge
                                    badgeContent={
                                      <AdminIcon fontSize="small" />
                                    }
                                    color="warning"
                                  >
                                    <EditIcon />
                                  </Badge>
                                ) : (
                                  <LockIcon />
                                )}
                              </IconButton>
                            </span>
                          </Tooltip>
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
            ) : (
              <TableRow>
                <TableCell
                  colSpan={
                    user?.role === "super_admin" ||
                    user?.role === "admin" ||
                    user?.role === "facility_admin"
                      ? 7
                      : 6
                  }
                  align="center"
                >
                  {searchQuery ||
                  statusFilter !== "all" ||
                  editabilityFilter !== "all" ? (
                    <>
                      <Typography variant="body1" sx={{ my: 3 }}>
                        No orders found matching your search criteria
                      </Typography>
                      <Button
                        variant="outlined"
                        onClick={() => {
                          setSearchQuery("");
                          setStatusFilter("all");
                          setEditabilityFilter("all");
                        }}
                      >
                        Clear Filters
                      </Button>
                    </>
                  ) : (
                    <>
                      <Typography variant="body1" sx={{ my: 3 }}>
                        No orders found. Create your first order to get started.
                      </Typography>
                      <Button
                        variant="contained"
                        color="primary"
                        startIcon={<AddIcon />}
                        onClick={() => navigate("/orders/create")}
                      >
                        Create First Order
                      </Button>
                    </>
                  )}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25, 50]}
          component="div"
          count={filteredOrders.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </TableContainer>

      <EFaxUploadModal
        open={efaxModalOpen}
        onClose={() => setEfaxModalOpen(false)}
      />
    </Box>
  );
};

export default OrdersList;
